import styles from "@styles/home-configurations.module.scss";
import { If } from "shared/components/control-statements";
import { Currency } from "shared/utils/Currency";
import {
  isPromotionalPrice,
  mountOldPrice,
  mountPrice,
} from "../Catalog/ProductItem";
import { PlusCircle } from "shared/components/Icons";
import { useMemo } from "react";
import { Environment } from "shared/core/Environment";

const buildCoupon = (coupon) => {
  if (Environment.id === 51) {
    coupon.description = !coupon.description ? `${coupon.code}` : coupon.description;
  } else coupon.description = !coupon.description
    ? `${coupon.code} ${coupon.pin ? `- ${coupon.pin}` : ""}`
    : coupon.description;

  return coupon;
};

const ScrollList = ({ layout, horizontalClass, verticalClass, children }) => {
  if (layout === "DEFAULT")
    return <div className={horizontalClass}>{children}</div>;

  if (layout === "VERTICAL_SCROLL")
    return <div className={verticalClass}>{children}</div>;

  return null;
};

const BannerSection = ({ data }) => {
  return (
    <div className={styles.previewSection}>
      <If cond={data.layout === "FULL_SCREEN"}>
        <div className={styles.fullScreen}>
          {data.items?.map((item) => {
            return (
              <div key={item.id} className={styles.banner}>
                <div className={styles.image}>
                  <If cond={item.mobile}>
                    <img
                      src={process.env.REACT_APP_ASSETS_URL + `/` + item.mobile}
                      alt={item.name}
                    />
                  </If>
                </div>
              </div>
            );
          })}
        </div>
      </If>
      <If cond={data.layout === "DEFAULT"}>
        <p className={styles.title}>{data.title}</p>
        <div className={styles.horizontalScroll}>
          <div className={styles.banners}>
            <If cond={data.presentationFormat === "HORIZONTAL_SCROLL"}>
              {data.items?.map((item) => {
                return (
                  <div key={item.id} className={styles.banner}>
                    <If cond={item.mobile}>
                      <div className={styles.image}>
                        <img
                          src={
                            process.env.REACT_APP_ASSETS_URL + `/` + item.mobile
                          }
                          alt={item.name}
                        />
                      </div>
                    </If>
                  </div>
                );
              })}
            </If>
            <If cond={data.presentationFormat === "CARROUSEL"}>
              {data.items.slice(0, 1)?.map((item) => {
                return (
                  <div key={item.id} className={styles.bannerFull}>
                    <If cond={item.mobile}>
                      <div className={styles.image}>
                        <img
                          src={
                            process.env.REACT_APP_ASSETS_URL + `/` + item.mobile
                          }
                          alt={item.name}
                        />
                      </div>
                    </If>
                  </div>
                );
              })}
            </If>
          </div>
        </div>
      </If>
    </div>
  );
};

const BrandsSection = ({ data, brands }) => {
  const brandMap = new Map(brands.map((brand) => [brand.id, brand]));

  return (
    <div className={styles.previewSection}>
      <p className={styles.title}>{data.title}</p>
      <ScrollList
        layout={data.layout}
        horizontalClass={styles.horizontalScroll}
        verticalClass={styles.verticalScroll}
      >
        <div className={styles.brands}>
          {data.items?.map((brandId) => {
            const brand = brandMap.get(brandId);
            return (
              <div key={brand.id} className={styles.brand}>
                <div className={styles.logo}>
                  <If cond={brand.logo}>
                    <img src={brand.logo} alt={brand.name} />
                  </If>
                </div>
                <p className={styles.name}>{brand.name}</p>
              </div>
            );
          })}
        </div>
      </ScrollList>
    </div>
  );
};

const ProductsSection = ({ data }) => {
  const builtItems = useMemo(() => {
    return data.items
      ?.map((item) => {
        return item;
      })
      .filter(Boolean);
  }, [data.items]);

  return (
    <div className={styles.previewSection}>
      <p className={styles.title}>{data.title}</p>
      <ScrollList
        layout={data.layout}
        horizontalClass={styles.horizontalScroll}
        verticalClass={styles.verticalScroll}
      >
        <div className={styles.products}>
          {builtItems.map((item) => {
            const { product } = item;

            const oldPrice = mountOldPrice(product);
            const hasAddons = (product.items || []).length > 0;
            const hasSizes = (product.sizes || []).length > 1;

            return (
              <div key={item.id} className={styles.product}>
                <div className={styles.image}>
                  <If cond={product.image}>
                    <img
                      src={process.env.REACT_APP_ASSETS_URL + `/` + product.image}
                      alt={product.name}
                    />
                  </If>
                </div>
                <div className={styles.details}>
                  <p className={styles.name}>{product.name}</p>
                  <div className="d-flex align-center justify-between">
                    {isPromotionalPrice(product) &&
                    oldPrice &&
                    !(hasAddons || hasSizes) ? (
                      <p className={styles.price}>
                        <strike>{Currency.formatCurrency(oldPrice)}</strike>
                        &nbsp;&nbsp;
                        {Currency.formatCurrency(mountPrice(product))}
                      </p>
                    ) : isPromotionalPrice(product) && !hasSizes ? (
                      <p className={styles.price}>
                        {oldPrice && (
                          <strike>{Currency.formatCurrency(oldPrice)}</strike>
                        )}
                      </p>
                    ) : hasAddons || hasSizes ? (
                      <p className={styles.price}>
                        {Currency.formatCurrency(mountPrice(product))}
                      </p>
                    ) : (
                      <p className={styles.price}>
                        {Currency.formatCurrency(mountPrice(product))}
                      </p>
                    )}
                    <PlusCircle />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollList>
    </div>
  );
};

const CouponsSection = ({ data, coupons }) => {
  const couponMap = useMemo(
    () => new Map(coupons.map((coupon) => [coupon.id, coupon])),
    [coupons]
  );

  const builtCoupons = useMemo(() => {
    const coupons = data.items?.map((c) => {
      const coupon = couponMap.get(c.id);

      if (!coupon) return null;

      return buildCoupon(coupon);
    });

    return coupons.filter(Boolean);
  }, [data.items, couponMap]);

  return (
    <div className={styles.previewSection}>
      <p className={styles.title}>{data.title}</p>
      <ScrollList
        layout={data.layout}
        horizontalClass={styles.horizontalScroll}
        verticalClass={styles.verticalScroll}
      >
        <div className={styles.coupons}>
          {builtCoupons.map((item) => {
            return (
              <div key={item.id} className={styles.coupon}>
                <div className={styles.details}>
                  <div className="d-flex flex-column">
                    <div className={styles.image}>
                      <If cond={item.image}>
                        <img
                          src={
                            process.env.REACT_APP_ASSETS_URL + `/` + item.image
                          }
                          alt={item.name}
                        />
                      </If>
                    </div>
                    <p className={styles.name}>{`${item.description}`}</p>
                  </div>
                  <div className={styles.useCoupon}>
                    <span>Usar cupom</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollList>
    </div>
  );
};

const Preview = ({ sections, brands, coupons }) => {
  return (
    <div className="flex-1 overflow-y-auto">
      {sections
        .filter((s) => s.active)
        .sort((a, b) => a.order - b.order)
        .map((section) => {
          switch (section.type) {
            case "BANNERS":
              return <BannerSection key={section.id} data={section} />;
            case "BRANDS":
              return (
                <BrandsSection
                  key={section.id}
                  brands={brands}
                  data={section}
                />
              );
            case "PRODUCTS":
              return <ProductsSection key={section.id} data={section} />;
            case "COUPONS":
              return (
                <CouponsSection
                  key={section.id}
                  coupons={coupons}
                  data={section}
                />
              );
            default:
              return null;
          }
        })}
    </div>
  );
};

export default Preview;
